import React, { createContext, useContext, useMemo, useRef } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { col, docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import {
  DEFAULT_CATEGORIES, sortCategories, categoryDocument, defaultCategoryDocument, locationDocument,
  categoryLinkDocument,
} from '../domain/categories';
import { useCollectionWindow } from './useCollectionWindow';
import { collectionEmptyAtServer, DEFAULTS_NEED_SERVER } from './serverConfirmed';

// Event categories and locations.
//
// As a free-text field on the event form, "Lesson", "lesson" and "Lessson"
// would be three different categories as far as any report is concerned. A
// managed list is what lets a category be counted, coloured and renamed in one
// place.
//
// A studio that has never opened this menu still needs categories, so the
// defaults below are offered when the collection is empty rather than being
// written at signup, and a studio that deletes them all gets them back rather
// than an empty dropdown.

// The list itself is in src/domain/categories.js, re-exported here for the
// callers that import it from this path. It lives there because the scheduled
// reminder worker reads a category's lead time (#87), and functions/ sees only
// src/domain (functions/scripts/sync-domain.mjs copies that directory alone).
export { DEFAULT_CATEGORIES };

const CategoriesContext = createContext(null);
const TRACK_LIMIT = 200;

export function CategoriesProvider({ children }) {
  const { user } = useAuth();

  // ── #207 U5: two collections, from whichever read path serves this session ──
  //
  // Neither listener passes `includeMetadataChanges`: this provider never reads
  // `pendingWrite`.
  const categoryWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.categories,
    limit: TRACK_LIMIT,
  });
  const locationWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.locations,
    limit: TRACK_LIMIT,
  });
  const stored = categoryWindow.docs;
  // By name, ascending, everywhere they are listed: the Locations tab and the
  // event drawer's Location select both read this. MMS's Locations table sorts
  // on "Name ↑" by default, as its Families table does (#504). The order is
  // `sortCategories`', the core's `sort_categories` (by label, then id), which
  // takes any `{ id, label }`.
  const locations = useMemo(() => sortCategories(locationWindow.docs), [locationWindow.docs]);

  // Have we actually read this studio's categories?
  //
  // `stored.length === 0` is doing double duty in the writers below: it means
  // "this studio is still on the built-in defaults, so materialise them". But
  // before the first read it is *also* what an unread collection looks like,
  // for every studio. A save in that window would re-create built-in categories
  // the studio had deleted, and removeCategory takes the same branch — writing
  // the defaults back and never issuing the delete it was asked for, so the
  // category would stay and the deleted ones return.
  //
  // `useCollectionWindow.hasRead` is that question, and on the store path it
  // answers it **offline too**: a `stale` window is a true answer about what
  // this studio holds, so a cold start can write against it. `appStore` keys
  // every receipt by `users/{uid}/…`, so it is this studio's.
  //
  // Mirrored into a ref so the writers can read it at call time rather than at
  // the render time of whatever closure the click came through.
  const hasRead = categoryWindow.hasRead;
  const hasReadRef = useRef(false);
  hasReadRef.current = hasRead;

  const value = useMemo(() => {
    // ── Whether this save also writes the built-ins (#631) ─────────────────
    //
    // `hasRead` stops a write before the first answer, but the first answer
    // can itself be a cold cache's `[]` — a fresh listener's first snapshot
    // often is (see src/hooks/useAddStudentForm.js) — and the read store keeps
    // it as a receipt. An empty list here does not prove the studio has no
    // categories, and materialising on it would set the six built-ins whole
    // over a studio's renamed ones and bring back deleted ones (#631).
    //
    // So an empty list asks the server (./serverConfirmed.js says why, and why
    // not create-only writes). Answers true when the server confirms the
    // studio has no category document, false when this device's list is not
    // empty or the server holds some (then the save writes only what it was
    // asked to, onto the studio's real list), and null when the server could
    // not be asked — the save is refused then, with `DEFAULTS_NEED_SERVER`,
    // because writing only the one category would drop the five built-ins the
    // teacher can see, and writing them could overwrite the studio's own.
    const builtInsToWrite = async () => {
      if (stored.length) return false;
      try {
        return await collectionEmptyAtServer(col(user.uid, COLLECTIONS.categories));
      } catch (err) {
        console.warn('[categories] could not ask the server whether this studio has categories, '
          + 'so nothing was written:', err);
        return null;
      }
    };

    // Defaults stand in until the studio saves its own. `isDefault` lets the
    // editor show them as suggestions rather than pretending they are records.
    //
    // **Both branches go through the same sort** (#111): `saveCategory` below
    // materialises all six built-ins on the first write of any one of them, so
    // sorting only the stored branch would re-order the drawer the teacher is
    // standing in on that first save. Alphabetical is MMS's order (see
    // `sortCategories` in domain/categories.js).
    //
    // The position of anything in here is a rendering detail and nothing else:
    // the two forms that need "the studio's Lesson" ask `defaultEventCategory`
    // for it by id (#141).
    const categories = sortCategories(
      stored.length
        ? stored
        : DEFAULT_CATEGORIES.map((c) => ({ ...c, isDefault: true }))
    );

    return {
      loaded: hasRead,
      categories,
      locations,
      // The locations' own first answer (#629): Categories & Locations says
      // "No locations yet." only once it has.
      locationsLoaded: locationWindow.hasRead,
      locationsFailed: locationWindow.failed,
      byId: (id) => categories.find((c) => c.id === id) || null,
      labelFor: (id) => categories.find((c) => c.id === id)?.label || 'Lesson',

      // This category's colour, or **null** for an id that names none.
      //
      // Not Lesson's blue for an unknown id (#109), unlike `labelFor` above: a
      // name has to be something, but a colour is a claim about which category
      // an event belongs to, and an event whose `categoryId` is absent or names
      // a deleted category belongs to none. The month and week-list chips are
      // tinted from this (MonthView.jsx), so an event carrying only a bare
      // `categoryLabel` (#110) is left untinted.
      colorFor: (id) => (id ? categories.find((c) => c.id === id)?.color ?? null : null),

      // Writing any category materialises the defaults alongside it, so the
      // studio does not silently lose the rest of the list the moment it adds
      // one of its own.
      //
      // **This does not check the label against the list** (#129). The
      // uniqueness rule lives in CategoriesDrawer, driven by `categoryErrors` in
      // domain/categories.js, where a duplicate name can be told to the person
      // who typed it. `categoryErrors` says why it is enforced there rather than
      // in `firestore.rules` or a deterministic id, and what it leaves open (two
      // offline devices adding the same name).
      //
      // The one other writer is scripts/backfill-category-ids.mjs, which links
      // documents to categories and never creates one.
      //
      // Answers `DEFAULTS_NEED_SERVER` when it was refused because the
      // built-ins could not be confirmed with the server (`builtInsToWrite`
      // above), and nothing when it wrote.
      saveCategory: async ({
        id, label, color, billable, allowMakeupCredits, reminderLeadHours,
      }) => {
        // Never write from a collection we have not read — see hasRead above.
        if (!user || !hasReadRef.current) return undefined;
        const builtIns = await builtInsToWrite();
        if (builtIns === null) return DEFAULTS_NEED_SERVER;
        const batch = writeBatch(db);
        if (builtIns) {
          for (const d of DEFAULT_CATEGORIES) {
            batch.set(
              docRef(user.uid, COLLECTIONS.categories, d.id),
              defaultCategoryDocument(d, serverTimestamp()),
            );
          }
        }
        const cid = id || freeId('cat');
        // `categoryDocument` in domain/categories.js, which says why each field
        // is always written whole on this `{ merge: true }` write.
        batch.set(docRef(user.uid, COLLECTIONS.categories, cid), categoryDocument({
          label, color, billable, allowMakeupCredits, reminderLeadHours,
        }, serverTimestamp()), { merge: true });
        await batch.commit();
        return undefined;
      },

      // Answers `DEFAULTS_NEED_SERVER` when it was refused (see
      // `builtInsToWrite`), and nothing otherwise — as `saveCategory` does.
      removeCategory: async (id) => {
        // Never write from a collection we have not read — see hasRead above.
        if (!user || !hasReadRef.current) return undefined;
        const builtIns = await builtInsToWrite();
        if (builtIns === null) return DEFAULTS_NEED_SERVER;
        const batch = writeBatch(db);
        if (builtIns) {
          for (const d of DEFAULT_CATEGORIES) {
            if (d.id === id) continue;
            batch.set(
              docRef(user.uid, COLLECTIONS.categories, d.id),
              defaultCategoryDocument(d, serverTimestamp()),
            );
          }
        } else {
          batch.delete(docRef(user.uid, COLLECTIONS.categories, id));
        }
        await batch.commit();
        return undefined;
      },

      saveLocation: async ({ id, label }) => {
        // Never write from a collection we have not read — see hasRead above.
        if (!user || !hasReadRef.current) return;
        const batch = writeBatch(db);
        batch.set(docRef(user.uid, COLLECTIONS.locations, id || freeId('loc')),
                  locationDocument({ label }, serverTimestamp()), { merge: true });
        await batch.commit();
      },

      removeLocation: async (id) => {
        // Never write from a collection we have not read — see hasRead above.
        if (!user || !hasReadRef.current) return;
        const batch = writeBatch(db);
        batch.delete(docRef(user.uid, COLLECTIONS.locations, id));
        await batch.commit();
      },

      // Commit a merge plan (#142). The plan is `planCategoryMerge`'s and is
      // built in the drawer; nothing is decided here.
      //
      // **Only a `merge` verdict writes.** The other three mean the plan is
      // stale or was never usable — the group has gone, the survivor is not in
      // it, no key was named — so a screen re-offered from a changed list
      // cannot delete a category nobody nominated.
      //
      // ── Rows first, categories second ────────────────────────────────────
      //
      // As in CategoriesDrawer's `saveEdits`: re-point the events and students
      // at the survivor, then delete the losers. An interruption leaves rows
      // moved and the loser still present, which can be merged again, and a
      // second run is a no-op on the moved rows because `planCategoryMerge`
      // filters on `categoryId`. Deleting first would leave rows pointing at an
      // id that resolves to nothing, and the duplicate group gone from the list,
      // so the repair could not be offered.
      //
      // A generic `{ collection, id }` write rather than a call into useEvents
      // and useStudents: every path this app builds is
      // `users/{uid}/{collection}/{id}`, the plan carries both halves, and
      // `occurrences` has no provider verb.
      //
      // Chunked at 400 because a Firestore batch takes 500 writes, as
      // `relabelCategory` in data/useEvents.jsx is.
      applyCategoryMerge: async (plan) => {
        // Never write from a collection we have not read — see hasRead above.
        if (!user || !hasReadRef.current) return 0;
        if (!plan || plan.verdict !== 'merge') return 0;

        const writes = (plan.writes || []).filter((w) => w?.collection && w?.id);
        for (let i = 0; i < writes.length; i += 400) {
          const batch = writeBatch(db);
          for (const w of writes.slice(i, i + 400)) {
            batch.set(
              docRef(user.uid, w.collection, w.id),
              categoryLinkDocument(w.categoryId, w.categoryLabel, serverTimestamp()),
              { merge: true },
            );
          }
          await batch.commit();
        }

        const remove = (plan.remove || []).filter((c) => c?.id);
        if (remove.length) {
          const batch = writeBatch(db);
          for (const c of remove) {
            batch.delete(docRef(user.uid, COLLECTIONS.categories, c.id));
          }
          await batch.commit();
        }
        return writes.length;
      },
    };
  }, [stored, locations, hasRead, locationWindow.hasRead, locationWindow.failed, user]);

  return <CategoriesContext.Provider value={value}>{children}</CategoriesContext.Provider>;
}

export function useCategories() {
  const ctx = useContext(CategoriesContext);
  if (!ctx) throw new Error('useCategories must be used within a CategoriesProvider');
  return ctx;
}
